import { type FC, memo } from "react";
import classNames from "classnames";
import ProductPreferredIndicator from "~/features/productDetails/components/ProductPreferredIndicator";
import { type IconSize } from "~/common/mui/icons/IconSize";
import { PopperTooltip } from "~/common/components/Tooltip";
import CopyToClipboard from "~/features/productDetails/components/CopyToClipboard";
import { useProductDetailsUrl } from "~/features/PackageRestriction/hooks/useProductDetailsUrl";
import type { ProductData } from "~/domains/product/types/ProductData";
import { Link, LinkType } from "~/common/components/Link";
import { Box, styled } from "~/common/mui";

interface DesignationProps {
  className?: string;
  designation: string;
  isPreferred?: boolean;
  reservePreferredSpace?: boolean;
  size: FontSize;
  iconSize?: IconSize;
  tooltipSide?: "left" | "right" | "bottom";
  title?: string;
  noCssColor?: boolean;
  as?: keyof JSX.IntrinsicElements;
  showCopyClipboard?: boolean;
  clickable?: boolean;
  product?: Pick<
    ProductData,
    "images" | "manufacturerLogoUrl" | "brandLogoUrl" | "url" | "isOptime" | "sapMaterialNumber" | "code"
  >;
}

const DesignationLabel = styled("span")({
  fontWeight: "bold",
  margin: 0,
});

const ProductDesignation: FC<DesignationProps> = (props) => {
  const {
    className,
    designation,
    isPreferred,
    reservePreferredSpace,
    size: fontCls,
    tooltipSide,
    iconSize,
    title,
    noCssColor,
    showCopyClipboard,
    as = "span",
    product = null,
    clickable = false,
  } = props;

  const Cmp = as;

  let label = (
    <DesignationLabel className={fontCls} data-test-id={"product-designation"}>
      {designation}
    </DesignationLabel>
  );

  const productUrl = product && useProductDetailsUrl(product);

  if (clickable && productUrl) {
    label = (
      <Link variant={LinkType.SECONDARY} href={productUrl}>
        {label}
      </Link>
    );
  }

  if (title) {
    label = (
      <PopperTooltip tooltipContent={<span className={"f-no-wrap"}>{title}</span>} className={"d-inline"}>
        {label}
      </PopperTooltip>
    );
  }

  const renderPreferred = isPreferred || reservePreferredSpace;

  return (
    <Box
      className={className}
      sx={{
        display: "flex",
        alignItems: "center",
        fontWeight: "bold",
        ...(!noCssColor && { color: "text.primary" }),
      }}
    >
      {renderPreferred && (
        <ProductPreferredIndicator
          className={classNames("icon-margin", !isPreferred && "v-hidden")}
          side={tooltipSide}
          iconSize={iconSize}
        />
      )}
      {label}
      {showCopyClipboard && <CopyToClipboard text={designation} iconSize={iconSize} />}
    </Box>
  );
};

export default memo(ProductDesignation);
